import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, BookOpenCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useClassOptions, ClassOptionItems } from "@/components/ClassOptions";

export default function GradebookPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [classFilter, setClassFilter] = useState("all");
  const [termFilter, setTermFilter] = useState("all");

  const isGlobal = ["master_admin", "super_admin"].includes(user?.role);

  async function loadMeta() {
    const { data } = await api.get("/branches");
    setBranches(data);
    setBranchId(user?.branch_id || data[0]?.id || "");
  }
  useEffect(() => { loadMeta().catch(() => {}); }, []);

  async function load() {
    const params = {};
    if (isGlobal && branchId) params.branch_id = branchId;
    if (classFilter !== "all") params.class_name = classFilter;
    if (termFilter !== "all") params.term = termFilter;
    const [g, s] = await Promise.all([api.get("/grades", { params }), api.get("/students", { params: isGlobal && branchId ? { branch_id: branchId } : {} })]);
    setGrades(g.data);
    setStudents(s.data);
  }
  useEffect(() => { if (branchId) load().catch(() => {}); }, [branchId, classFilter, termFilter]); // eslint-disable-line

  const classOpts = useClassOptions(branchId);

  async function remove(g) {
    if (!window.confirm("Delete this grade?")) return;
    try { await api.delete(`/grades/${g.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Academics</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Gradebook</h1>
          <p className="text-slate-500 mt-1.5">{grades.length} grade entries recorded.</p>
        </div>
        <AddGradeDialog students={students} onAdded={load} />
      </div>

      <Card className="border-violet-100 mb-5">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {isGlobal && (
            <div>
              <Label>Branch</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger data-testid="grade-branch" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Class</Label>
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger data-testid="grade-class-filter" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                <ClassOptionItems opts={classOpts} />
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Term</Label>
            <Select value={termFilter} onValueChange={setTermFilter}>
              <SelectTrigger data-testid="grade-term-filter" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Terms</SelectItem>
                <SelectItem value="Term 1">Term 1</SelectItem>
                <SelectItem value="Term 2">Term 2</SelectItem>
                <SelectItem value="Term 3">Term 3</SelectItem>
                <SelectItem value="Final">Final</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {grades.length === 0 ? (
        <Card className="border-dashed border-2 border-violet-200"><CardContent className="py-16 text-center">
          <BookOpenCheck className="mx-auto text-violet-400" size={44} />
          <h3 className="font-display text-xl font-bold text-slate-900 mt-4">No grades yet</h3>
          <p className="text-slate-500 text-sm mt-1">Click <em>Add Grade</em> to record your first assessment.</p>
        </CardContent></Card>
      ) : (
        <Card className="border-violet-100 overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="bg-violet-50 text-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Student</th>
                    <th className="text-left px-3 py-3 font-semibold">Class</th>
                    <th className="text-left px-3 py-3 font-semibold">Subject</th>
                    <th className="text-left px-3 py-3 font-semibold">Term</th>
                    <th className="text-right px-3 py-3 font-semibold">Marks</th>
                    <th className="text-right px-3 py-3 font-semibold">%</th>
                    <th className="text-center px-3 py-3 font-semibold">Grade</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {grades.map((g) => (
                    <tr key={g.id} data-testid={`grade-${g.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{g.student_name}</div>
                        <div className="text-xs text-slate-500 font-mono">{g.admission_number}</div>
                      </td>
                      <td className="px-3 py-3"><Badge variant="outline">{g.class_name}/{g.section}</Badge></td>
                      <td className="px-3 py-3 font-medium">{g.subject}</td>
                      <td className="px-3 py-3 text-slate-600">{g.term}</td>
                      <td className="text-right px-3 py-3 font-semibold">{g.marks} / {g.max_marks}</td>
                      <td className="text-right px-3 py-3">{g.percent}%</td>
                      <td className="text-center px-3 py-3">
                        <Badge className={gradeColor(g.grade)}>{g.grade}</Badge>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button data-testid={`delete-grade-${g.id}`} onClick={() => remove(g)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </AppShell>
  );
}

function gradeColor(g) {
  if (g === "A+" || g === "A") return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
  if (g === "B") return "bg-violet-100 text-violet-800 hover:bg-violet-100";
  if (g === "C") return "bg-amber-100 text-amber-800 hover:bg-amber-100";
  return "bg-rose-100 text-rose-800 hover:bg-rose-100";
}

function AddGradeDialog({ students, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", subject: "Mathematics", term: "Term 1", marks: 85, max_marks: 100, remarks: "" });
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/grades", { ...form, marks: Number(form.marks), max_marks: Number(form.max_marks) });
      toast.success("Grade recorded");
      setOpen(false);
      onAdded?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-grade-btn" className="bg-violet-600 hover:bg-violet-700 h-11 px-5 font-semibold">
          <Plus size={18} className="mr-2" /> Add Grade
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-xl">Record a grade</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label>Student</Label>
            <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
              <SelectTrigger data-testid="grade-student-select" className="mt-1.5"><SelectValue placeholder="Choose student" /></SelectTrigger>
              <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} • {s.admission_number}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Subject</Label>
              <Input data-testid="grade-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 input-focus" required />
            </div>
            <div>
              <Label>Term</Label>
              <Select value={form.term} onValueChange={(v) => setForm({ ...form, term: v })}>
                <SelectTrigger data-testid="grade-term" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Term 1">Term 1</SelectItem>
                  <SelectItem value="Term 2">Term 2</SelectItem>
                  <SelectItem value="Term 3">Term 3</SelectItem>
                  <SelectItem value="Final">Final</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Marks</Label>
              <Input data-testid="grade-marks" type="number" value={form.marks} onChange={(e) => setForm({ ...form, marks: e.target.value })} className="mt-1.5 input-focus" required />
            </div>
            <div>
              <Label>Max Marks</Label>
              <Input data-testid="grade-max" type="number" value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: e.target.value })} className="mt-1.5 input-focus" required />
            </div>
          </div>
          <div>
            <Label>Remarks</Label>
            <Input data-testid="grade-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1.5 input-focus" />
          </div>
          <Button data-testid="save-grade-btn" type="submit" disabled={saving || !form.student_id} className="w-full h-11 bg-violet-600 hover:bg-violet-700">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Save Grade"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
